import { useMutation, useQueryClient } from '@tanstack/react-query';

import { AxiosError } from 'axios';

import {
  AskAIRequestAnswerApiError,
  AskAIRequestAnswerApiResponse,
} from '../apis';

import { clientAxios } from '@/shared/api';
import { ApiResponse, ArticleItemProps } from '@/shared/types';
import { PATH } from '@/shared/constants';

type AskAIRequestAnswerMutationResponse =
  | {
      questionHashKey: string;
      answerText: string;
      questionText: string;
      contentsItems: ArticleItemProps[];
    }
  | 'noContent';

const useAskAIRequestAnswerMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    AskAIRequestAnswerMutationResponse,
    AxiosError<AskAIRequestAnswerApiError>,
    string
  >({
    mutationFn: async (questionText: string) => {
      const { data, status } = await clientAxios.post<
        ApiResponse<AskAIRequestAnswerApiResponse>
      >('/askAIRequestAnswerApi/', { questionText });

      if (status === 204) return 'noContent';

      const { answer_text, question_text, quoted, question_hash_key } =
        data.data;

      const contentsItems: ArticleItemProps[] = quoted.map((item) => {
        const {
          contents,
          news: {
            id,
            author_name,
            author_image,
            author_url_identifier,
            category_kor,
            category_flag,
            is_popular_news,
            read_time,
            repr_date_published,
            thumbnail_image,
            title,
          },
        } = item;

        return {
          id,
          href: `${PATH.DETAIL}/${id}`,
          title: title,
          category: category_kor,
          categoryHref: `${PATH.LIST}/${category_flag}`,
          description: contents.replace(/&nbsp;/g, ' '),
          thumbnail: thumbnail_image,
          articleInfo: {
            time: read_time,
            date: repr_date_published,
            isPopular: is_popular_news,
          },
          author: {
            name: author_name,
            href: `${PATH.HOME}/@${author_url_identifier}`,
            imgUrl: author_image,
          },
        };
      });

      const response: AskAIRequestAnswerMutationResponse = {
        questionHashKey: question_hash_key,
        answerText: answer_text,
        questionText: question_text,
        contentsItems,
      };

      return response;
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['askAI', 'questions'],
      });
    },
  });
};

export default useAskAIRequestAnswerMutation;
